import React, { useState } from 'react';
import { Alert, Box, Button, Snackbar, Stack, Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { useForm, useWatch } from 'react-hook-form';

import TopBar from '../../../components/TopBar';
import AccountingPeriodPicker from '../../../components/form/AccountingPeriodPicker';
import ReportTable from '../components/ReportTable';
import VatTrnMultiSelect from '../components/VatTrnMultiSelect';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useGetDocumentReportsHandler } from '../dataHandlers/getReportsHandler';
import { useCreateReportsJobHandler } from '../dataHandlers/createReportJobHandler';
import { useGetEligibleVatTrnsHandler } from '../dataHandlers/getEligibleVatTrnsHandler';
import { reportTypes } from '../../../constants/appConstants';
import FeatureNotSupportedDialog from '../../common/FeatureNotSupportedDialog';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import VATReportDesclaimerBanner from '../components/VATReportDesclaimerBanner';
import useSubscriptionCheck from '../../common/SubscriptionCheck';
import { convertMonthYearToDate, parseAccountingPeriodValue } from '../../../utils/formatDate';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAccessDeniedMessage } from '../../../hooks/useAccessDeniedMessage';
import { useActionDeniedSnackbar } from '../../../hooks/useActionDeniedSnackbar';
import { CREATE_VAT_REPORT } from '../../../constants/eInvoicePermissions';
import { getVatReportGenerateAccess } from '../utils/getVatReportGenerateAccess';
import appConfig from '../../../config/appConfig';

const tooltipSlotProps = {
  tooltip: { sx: { maxWidth: 300 } },
};

const VATReportPage = () => {
  useDocumentTitle('VAT Returns');
  const { documentReports: dr, validations } = useTranslatedMessages();
  const vatReport = dr.vat_report;
  const reportValidations = validations.report ?? {};
  useDocumentTitle(vatReport.feature_not_supported_title);
  const { org } = useSelector((state) => state.org);
  const user = useSelector((state) => state.user.user);
  const isOrganizationOwner = Array.isArray(user?.roles) && user.roles.includes('ORGANIZATION_OWNER');
  const { checkSubscription, SubscriptionModal } = useSubscriptionCheck();
  const { hasEInvoicePermission } = usePermissions();
  const accessDeniedMessage = useAccessDeniedMessage();
  const showActionDeniedSnackbar = useActionDeniedSnackbar();
  const canCreate = hasEInvoicePermission(CREATE_VAT_REPORT);
  const rootUserEmail = org?.root_user_email ?? '';
  const { hasAllSalesPurchaseScope, tooltipMessage } = getVatReportGenerateAccess({
    hasEInvoicePermission,
    vatReportLabels: vatReport,
    adminEmail: rootUserEmail,
  });
  const userEmail = org?.user?.email || '';
  const [state, setState] = useState({
    page: 0,
    size: 10,
    localSuccessSnackbarOpen: false,
  });

  const {
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      startDate: null,
      endDate: null,
      vatNumber: '',
    },
  });

  const subscription = useSelector((state) => state.subscription.subscription);
  const isVatReportFeatureEnabled = subscription?.features?.enable_vat_report;
  const isPhase2FeatureEnabled = subscription?.features?.enable_phase2;
  const startDate = useWatch({ control, name: 'startDate' });
  const endDate = useWatch({ control, name: 'endDate' });
  const vatNumber = useWatch({ control, name: 'vatNumber' }) ?? '';
  const { vatTrns, isLoading: vatTrnsLoading } = useGetEligibleVatTrnsHandler({
    enabled: Boolean(isVatReportFeatureEnabled),
  });
  const hasEligibleTrns = vatTrns.length > 0;
  const filtersDisabled = !hasEligibleTrns;
  const canGenerateDatesAndTrn = !!startDate && !!endDate && !!vatNumber && hasEligibleTrns;
  const generateTooltip = filtersDisabled
    ? (vatReport.no_eligible_trn_tooltip ??
      'You need access to all business profiles for at least one VAT number to generate a VAT return.')
    : !canCreate
      ? accessDeniedMessage
      : tooltipMessage;

  const currentMonth = dayjs().startOf('month');
  const startDateForMinDate = parseAccountingPeriodValue(startDate);

  const { page, size, localSuccessSnackbarOpen } = state;

  const { reports, totalReports, loading } = useGetDocumentReportsHandler({
    searchQuery: { page, size, type: reportTypes.VAT_REPORT },
  });

  const { onSubmit } = useCreateReportsJobHandler();
  const handleGenerateReport = (data) => {
    if (filtersDisabled) {
      return;
    }
    if (!canCreate) {
      showActionDeniedSnackbar();
      return;
    }
    if (!hasAllSalesPurchaseScope) {
      return;
    }
    if (!checkSubscription()) {
      return;
    }
    if (!data.startDate) {
      setError('startDate', { type: 'required', message: reportValidations.start_period_required });
    }
    if (!data.endDate) {
      setError('endDate', { type: 'required', message: reportValidations.end_period_required });
    }
    if (!data.vatNumber) {
      return;
    }
    if (!data.startDate || !data.endDate) {
      return;
    }
    const createdAfter = convertMonthYearToDate(data.startDate, true);
    const createdBefore = convertMonthYearToDate(data.endDate, false);
    if (!createdAfter || !createdBefore) {
      return;
    }
    const reportData = {
      created_after: createdAfter,
      created_before: createdBefore,
      document_type_code: null,
      type: reportTypes.VAT_REPORT,
      vat: data.vatNumber,
    };
    onSubmit(reportData);
    reset();
  };

  return isVatReportFeatureEnabled ? (
    <Stack direction="column" sx={{ mt: '-1rem' }}>
      <TopBar title={vatReport.page_title}>
        <>
          <Box>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems="top"
              justifyContent="space-between"
              useFlexGap
              flexWrap="wrap"
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flex: { md: 1 }, minWidth: 0 }}>
                <Tooltip
                  title={filtersDisabled ? generateTooltip : ''}
                  arrow
                  placement="auto"
                  slotProps={tooltipSlotProps}
                >
                  <Box sx={{ minWidth: 280, maxWidth: 420, flex: 1 }}>
                    <VatTrnMultiSelect
                      options={vatTrns}
                      selected={vatNumber ? [vatNumber] : []}
                      isLoading={vatTrnsLoading}
                      disabled={filtersDisabled}
                      label={vatReport.vat_number_trn ?? 'VAT Number (TRN) *'}
                      selectAllLabel={vatReport.select_all_vat_numbers}
                      onChange={(selected) => setValue('vatNumber', selected[selected.length - 1] ?? '')}
                    />
                  </Box>
                </Tooltip>
                <Box sx={{ minWidth: 200, maxWidth: 280 }}>
                  <AccountingPeriodPicker
                    control={control}
                    name="startDate"
                    label={vatReport.start_period}
                    errors={errors}
                    disabled={filtersDisabled}
                    {...(currentMonth && { maxDate: currentMonth })}
                  />
                </Box>
                <Box sx={{ minWidth: 200, maxWidth: 280 }}>
                  <AccountingPeriodPicker
                    control={control}
                    name="endDate"
                    label={vatReport.end_period}
                    errors={errors}
                    disabled={filtersDisabled}
                    {...(startDateForMinDate && { minDate: startDateForMinDate })}
                    {...(currentMonth && { maxDate: currentMonth })}
                  />
                </Box>
              </Stack>
              <Tooltip title={generateTooltip} arrow placement="auto" slotProps={tooltipSlotProps}>
                <span style={{ display: 'inline-flex' }}>
                  <Button
                    id="generate-vat-report"
                    variant="contained"
                    disabled={
                      !canGenerateDatesAndTrn || !canCreate || !hasAllSalesPurchaseScope || loading || filtersDisabled
                    }
                    onClick={handleSubmit(handleGenerateReport)}
                    sx={{ minWidth: 160 }}
                  >
                    {loading ? vatReport.generating : vatReport.generate_vat_report}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        </>
      </TopBar>
      <VATReportDesclaimerBanner />
      {!isPhase2FeatureEnabled && (
        <Alert severity="info" sx={{ borderRadius: 0, my: 2 }}>
          {vatReport.vat_data_updated_info}{' '}
          <a
            href={`mailto:${appConfig.brandEmail}`}
            style={{ fontWeight: 600, color: 'inherit', textDecoration: 'underline' }}
          >
            {appConfig.brandEmail}
          </a>
          .
        </Alert>
      )}
      <ReportTable
        reports={reports}
        loading={loading}
        totalReports={totalReports}
        showCreatedBy={isOrganizationOwner}
        useAuthenticatedDownload
        downloadDefaultFileName="vat_report.xlsx"
        downloadInsufficientAccessMessage={vatReport.download_insufficient_access}
        onPageChange={(newPage) => setState((prev) => ({ ...prev, page: newPage }))}
        onPageSizeChange={(newSize) => setState((prev) => ({ ...prev, size: newSize }))}
      />
      <SubscriptionModal />
      <Snackbar
        open={localSuccessSnackbarOpen}
        onClose={() => setState((prev) => ({ ...prev, localSuccessSnackbarOpen: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setState((prev) => ({ ...prev, localSuccessSnackbarOpen: false }))}
        >
          {vatReport.report_initiated_success}{' '}
          <a href={`mailto:${userEmail}`} style={{ fontWeight: 'bold', color: 'inherit', textDecoration: 'underline' }}>
            {userEmail}
          </a>{' '}
          {vatReport.report_initiated_success_suffix}
        </Alert>
      </Snackbar>
    </Stack>
  ) : (
    <FeatureNotSupportedDialog
      title={vatReport.feature_not_supported_title}
      description={vatReport.feature_not_supported_description}
    />
  );
};

export default VATReportPage;
